import clsx from 'clsx';
import { FormikErrors } from 'formik';
import { PackageIcon, X } from 'lucide-react';
import { useDroppable } from '@dnd-kit/core';

import { SourceId } from '@/react/portainer/gitops/sources/types';

import { Card } from '@@/primitives/Card';
import { Input } from '@@/form-components/Input';
import { FormControl } from '@@/form-components/FormControl';
import { PortainerSelect } from '@@/form-components/PortainerSelect';
import { Button } from '@@/buttons';
import { Badge } from '@@/Badge';
import { FormError } from '@@/form-components/FormError';

import { Artifact } from '../../types';

import { ArtifactsSummary } from './ArtifactsSummary';
import { encodeArtifactDropId } from './dragId';

type ArtifactError = string | FormikErrors<Artifact> | undefined;

type Props = {
  artifact: Artifact;
  errors: ArtifactError;
  index: number;
  otherArtifactNames: Array<string>;
  isActive: boolean;
  isNameReadOnly: boolean;
  canRemove: boolean;
  sourceNameById: Map<SourceId, string>;
  onActivate: () => void;
  onChange: (changes: Partial<Artifact>) => void;
  onRemove: () => void;
};

function FieldError({
  errors,
  k,
  className,
}: {
  errors: ArtifactError;
  k: keyof FormikErrors<Artifact>;
  className?: string;
}) {
  if (!errors || typeof errors === 'string') {
    return null;
  }

  const message = errors[k];
  if (typeof message !== 'string') {
    return null;
  }

  return (
    <FormError className={clsx('m-0 shrink-0', className)}>{message}</FormError>
  );
}

export function ArtifactGroup({
  artifact,
  errors,
  index,
  otherArtifactNames,
  isActive,
  isNameReadOnly,
  canRemove,
  sourceNameById,
  onActivate,
  onChange,
  onRemove,
}: Props) {
  const { setNodeRef, isOver } = useDroppable({
    id: encodeArtifactDropId(index),
  });

  return (
    <Card.Container
      ref={setNodeRef}
      aria-label={`Artifact ${index + 1}`}
      className={clsx(
        isActive && 'border-2 border-blue-9!',
        isOver && 'ring-2 ring-blue-8'
      )}
    >
      <Card.Header
        icon={PackageIcon}
        title={
          <div className="flex flex-row items-center gap-2">
            <Input
              value={artifact.name}
              onChange={(e) => onChange({ name: e.target.value })}
              onFocus={onActivate}
              placeholder={`artifact-${index + 1}`}
              aria-label={`Artifact ${index + 1} Name`}
              data-cy={`artifact-name-input-${index}`}
              readOnly={isNameReadOnly}
              disabled={isNameReadOnly}
            />
            <FieldError errors={errors} k="name" />
          </div>
        }
        actions={
          <>
            {isActive ? (
              <Badge type="infoSecondary">Selecting files</Badge>
            ) : (
              <Button
                type="button"
                color="link"
                size="xsmall"
                onClick={onActivate}
                data-cy={`artifact-select-button-${index}`}
              >
                Select files here
              </Button>
            )}
            {canRemove && (
              <Button
                type="button"
                color="dangerlight"
                size="xsmall"
                icon={X}
                onClick={onRemove}
                aria-label={`Remove artifact ${index + 1}`}
                data-cy={`artifact-remove-button-${index}`}
              />
            )}
          </>
        }
      />
      <Card.Body className="flex flex-col gap-3">
        {typeof errors === 'string' && (
          <FormError className="m-0">{errors}</FormError>
        )}

        <FormControl
          inputId={`artifact-${index}-dependencies`}
          label="Depends on"
        >
          <PortainerSelect<string>
            inputId={`artifact-${index}-dependencies`}
            data-cy={`artifact-dependencies-select-${index}`}
            isMulti
            value={artifact.dependencies}
            onChange={(dependencies) => onChange({ dependencies })}
            options={otherArtifactNames.map((name) => ({
              value: name,
              label: name,
            }))}
            placeholder="No dependencies"
            noOptionsMessage={() => 'No other artifacts to depend on'}
          />
          <FieldError className="mt-1" errors={errors} k="dependencies" />
        </FormControl>

        <FieldError errors={errors} k="files" />

        <ArtifactsSummary
          files={artifact.files}
          sourceNameById={sourceNameById}
          onReorder={(files) => onChange({ files })}
          onRemove={(fileIndex) =>
            onChange({
              files: artifact.files.filter((_, i) => i !== fileIndex),
            })
          }
        />
      </Card.Body>
    </Card.Container>
  );
}
